import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Info,
  LocationOn,
  Business,
  CheckCircle,
  Warning,
  PersonOutline,
  TrendingUp,
  Close,
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import debounce from 'debounce';

interface VenueSearchResult {
  id: string;
  name: string;
  businessName?: string;
  address: string;
  placeId?: string;
  totalViews: number;
  totalSearches: number;
  totalFavorites: number;
  ownerId?: string;
  venueAdminId?: string;
  hasPortalAccess: boolean;
  createdBy: string;
  createdAt: any;
}

interface ImprovedVenueSearchProps {
  currentAdminId: string;
  onVenueLinked: (venueId: string, venueData: VenueSearchResult) => void;
  onCreateNew: () => void;
}

const ImprovedVenueSearch: React.FC<ImprovedVenueSearchProps> = ({
  currentAdminId,
  onVenueLinked,
  onCreateNew,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<VenueSearchResult[]>([]);
  const [error, setError] = useState('');
  const [linking, setLinking] = useState<string | null>(null);

  /**
   * Normalize text for better matching
   * - Remove punctuation and extra spaces
   * - Convert to lowercase
   * - Handle common variations
   */
  const normalizeText = (text: string | null | undefined): string => {
    if (!text || typeof text !== 'string'){
      return '';
    }
    return text
      .toLowerCase()
      .replace(/[''`]/g, '') // Remove apostrophes
      .replace(/[^\w\s]/g, ' ') // Replace punctuation with space
      .replace(/\s+/g, ' ') // Collapse multiple spaces
      .trim();
  };

  /**
   * Calculate text similarity score (0-1)
   * Uses multiple strategies for better matching
   */
  const calculateSimilarity = (text1: string, text2: string): number => {
    const norm1 = normalizeText(text1);
    const norm2 = normalizeText(text2);

    // Exact match after normalization
    if (norm1 === norm2) return 1.0;

    // One contains the other
    if (norm1.includes(norm2) || norm2.includes(norm1)) {
      const longer = norm1.length > norm2.length ? norm1 : norm2;
      const shorter = norm1.length > norm2.length ? norm2 : norm1;
      return shorter.length / longer.length;
    }

    // Word-level matching
    const words1 = norm1.split(' ');
    const words2 = norm2.split(' ');
    const commonWords = words1.filter(w => words2.includes(w)).length;
    const totalWords = Math.max(words1.length, words2.length);
    
    if (commonWords > 0) {
      return commonWords / totalWords;
    }

    // Levenshtein distance for fuzzy matching
    return 1 - (levenshteinDistance(norm1, norm2) / Math.max(norm1.length, norm2.length));
  };

  /**
   * Calculate Levenshtein distance between two strings
   */
  const levenshteinDistance = (str1: string, str2: string): number => {
    const m = str1.length;
    const n = str2.length;
    const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] = Math.min(dp[i - 1][j - 1] + 1, dp[i - 1][j] + 1, dp[i][j - 1] + 1);
        }
      }
    }

    return dp[m][n];
  };

  /**
   * Determine venue status for display
   */
  const getVenueStatus = (venue: VenueSearchResult): {
    label: string;
    color: 'success' | 'warning' | 'error' | 'info' | 'default';
    description: string;
    canClaim: boolean;
  } => {
    // Venue is owned by current admin
    if (venue.ownerId === currentAdminId) {
      return {
        label: 'Your Venue',
        color: 'success',
        description: 'This venue is already linked to your account',
        canClaim: false,
      };
    }

    // Venue has a portal-registered admin (different from current)
    if (venue.ownerId && venue.hasPortalAccess) {
      return {
        label: 'Claimed by Another Admin',
        color: 'error',
        description: 'This venue is managed by another business owner',
        canClaim: false,
      };
    }

    // Venue was created by users searching (no admin portal access)
    if (!venue.ownerId || !venue.hasPortalAccess) {
      return {
        label: 'Available to Claim',
        color: 'info',
        description: 'This venue was created by app users. You can claim it to access analytics.',
        canClaim: true,
      };
    }

    return {
      label: 'Unknown Status',
      color: 'default',
      description: 'Unable to determine venue status',
      canClaim: false,
    };
  };

  /**
   * Search venues with improved matching
   */
  const searchVenues = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2) {
        setResults([]);
        return;
      }

      setSearching(true);
      setError('');

      try {
        console.log('🔍 Searching venues:', searchTerm);

        // Get all venues (for small datasets)
        // For large datasets, consider implementing server-side search
        const venuesRef = collection(db, 'venues');
        const snapshot = await getDocs(venuesRef);

        const matches: Array<{ venue: VenueSearchResult; score: number }> = [];

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const venueName = data.name || data.businessName || '';
          const businessName = data.businessName || '';
          const address = data.address || '';

          if (!venueName){
            return;
          }

          // Calculate similarity scores
          const nameScore = calculateSimilarity(searchTerm, venueName);
          const businessScore = calculateSimilarity(searchTerm, businessName);
          const addressScore = calculateSimilarity(searchTerm, address) * 0.5; // Address less important

          const maxScore = Math.max(nameScore, businessScore, addressScore);

          // Include if similarity is above threshold (0.5 = 50%)
          if (maxScore >= 0.5) {
            matches.push({
              venue: {
                id: doc.id,
                name: venueName,
                businessName,
                address,
                placeId: data.location?.placeId || data.placeId,
                totalViews: data.totalViews || 0,
                totalSearches: data.totalSearches || 0,
                totalFavorites: data.totalFavorites || 0,
                ownerId: data.ownerId,
                venueAdminId: data.venueAdminId,
                hasPortalAccess: data.isVenuePortalEnabled || false,
                createdBy: data.createdBy || 'unknown',
                createdAt: data.createdAt,
              },
              score: maxScore,
            });
          }
        });

        // Sort by similarity score (highest first)
        matches.sort((a, b) => b.score - a.score);

        const topResults = matches.slice(0, 10).map(m => m.venue);

        console.log(`✅ Found ${topResults.length} matching venues`);
        setResults(topResults);

      } catch (err: any) {
        console.error('❌ Search error:', err);
        setError(`Search failed: ${err.message}`);
      } finally {
        setSearching(false);
      }
    }, 500),
    [currentAdminId]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    searchVenues(value);
  };

  /**
   * Link venue to current admin
   */
  const handleClaimVenue = async (venue: VenueSearchResult) => {
    setLinking(venue.id);
    setError('');

    try {
      console.log('🔗 Claiming venue:', venue.id);

      const venueRef = doc(db, 'venues', venue.id);

      // Update venue with ownership
      await updateDoc(venueRef, {
        ownerId: currentAdminId,
        venueAdminId: currentAdminId,
        isVenuePortalEnabled: true,
        ownership: {
          adminId: currentAdminId,
          claimedAt: Timestamp.now(),
          claimMethod: 'portal_registration_search',
          previousCreatedBy: venue.createdBy,
        },
        updatedAt: Timestamp.now(),
      });

      // Update venue admin's venuesOwned array
      const venueAdminQuery = query(
        collection(db, 'venue_admins'),
        where('adminId', '==', currentAdminId)
      );
      const adminSnapshot = await getDocs(venueAdminQuery);

      if (!adminSnapshot.empty) {
        const adminDoc = adminSnapshot.docs[0];
        const adminData = adminDoc.data();
        const currentVenues = adminData.venuesOwned || [];

        if (!currentVenues.includes(venue.id)) {
          await updateDoc(doc(db, 'venue_admins', adminDoc.id), {
            venuesOwned: [...currentVenues, venue.id],
            venueCount: currentVenues.length + 1,
            updatedAt: Timestamp.now(),
          });
        }
      }

      console.log('✅ Venue claimed successfully');
      onVenueLinked(venue.id, venue);

    } catch (err: any) {
      console.error('❌ Claim error:', err);
      setError(`Failed to claim venue: ${err.message}`);
    } finally {
      setLinking(null);
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'Unknown';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-AU', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Box>
      {/* Step Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight="600" gutterBottom>
          Search for Your Venue
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Before creating a new venue, let's check if it already exists in our system. 
          If it does, you can claim ownership instead of creating a duplicate.
        </Typography>
      </Box>

      {/* Search Input */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              fullWidth
              label="Business Name"
              placeholder="Enter your venue or business name..."
              value={searchQuery}
              onChange={handleSearchChange}
              helperText="Try different variations like 'Gallon', 'Gallons', or 'Gallon's' - we'll find similar matches"
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: searchQuery && (
                  <IconButton size="small" onClick={() => {
                    setSearchQuery('');
                    setResults([]);
                  }}>
                    <Close />
                  </IconButton>
                ),
              }}
            />
            <Button
              variant="contained"
              size="large"
              startIcon={<Search />}
              onClick={() => searchVenues(searchQuery)}
              disabled={!searchQuery || searching}
              sx={{ minWidth: 120, height: 56 }}
            >
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {searching && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress size={40} />
              <Typography ml={2} color="text.secondary">
                Searching for similar venues...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Search Results */}
      {!searching && results.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
              We found {results.length} similar venue{results.length !== 1 ? 's' : ''}. 
              If one of these is your business, you can claim it instead of creating a new venue.
            </Alert>

            <List>
              {results.map((venue, index) => {
                const status = getVenueStatus(venue);
                
                return (
                  <React.Fragment key={venue.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        py: 2,
                      }}
                      secondaryAction={
                        status.canClaim ? (
                          <Button
                            variant="contained"
                            onClick={() => handleClaimVenue(venue)}
                            disabled={!!linking}
                            startIcon={linking === venue.id ? <CircularProgress size={16} /> : <CheckCircle />}
                          >
                            {linking === venue.id ? 'Claiming...' : 'Claim This Venue'}
                          </Button>
                        ) : status.label === 'Your Venue' ? (
                          <Chip label="Already Yours" color="success" icon={<CheckCircle />} />
                        ) : (
                          <Chip 
                            label="Already Claimed" 
                            color="error"
                            variant="outlined"
                          />
                        )
                      }
                    >
                      {/* Venue Name and Status */}
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Business color="primary" />
                        <Typography variant="h6" fontWeight="600">
                          {venue.name}
                        </Typography>
                        <Chip 
                          label={status.label}
                          color={status.color}
                          size="small"
                        />
                      </Box>

                      {/* Business Name (if different) */}
                      {venue.businessName && venue.businessName !== venue.name && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 0.5 }}>
                          Business: {venue.businessName}
                        </Typography>
                      )}

                      {/* Address */}
                      <Box display="flex" alignItems="center" gap={0.5} ml={4} mb={1}>
                        <LocationOn fontSize="small" sx={{ fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          {venue.address}
                        </Typography>
                      </Box>

                      {/* Analytics Summary */}
                      {(venue.totalViews > 0 || venue.totalSearches > 0) && (
                        <Box display="flex" gap={2} ml={4} mb={1}>
                          <Tooltip title="Total views from app users">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <TrendingUp fontSize="small" sx={{ fontSize: 16 }} />
                              <Typography variant="caption" color="text.secondary">
                                {venue.totalViews} views
                              </Typography>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Times searched by users">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Search fontSize="small" sx={{ fontSize: 16 }} />
                              <Typography variant="caption" color="text.secondary">
                                {venue.totalSearches} searches
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      )}

                      {/* Status Description */}
                      <Alert 
                        severity={status.color === 'error' ? 'warning' : 'info'} 
                        icon={status.color === 'info' ? <Info /> : status.color === 'error' ? <Warning /> : <PersonOutline />}
                        sx={{ mt: 1 }}
                      >
                        {status.description}
                        {status.canClaim && venue.totalViews > 0 && (
                          <Typography variant="caption" display="block" mt={0.5}>
                            <strong>Bonus:</strong> By claiming this venue, you'll inherit {venue.totalViews} views 
                            and {venue.totalSearches} searches from existing app users!
                          </Typography>
                        )}
                      </Alert>

                      {/* Metadata */}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
                        Created: {formatDate(venue.createdAt)} • 
                        {venue.createdBy === 'flutter_app' ? ' Added by app users' : ' Registered via portal'}
                      </Typography>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </CardContent>
        </Card>
      )}

      {/* No Results */}
      {!searching && searchQuery.length >= 2 && results.length === 0 && (
        <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
          No similar venues found. You can proceed to create a new venue listing.
        </Alert>
      )}

      {/* Create New Button */}
      <Card>
        <CardContent>
          <Box textAlign="center">
            <Typography variant="body1" gutterBottom>
              Don't see your venue? No problem!
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Continue with the form below to create a new venue listing. 
              Our system will guide you through the setup process.
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={onCreateNew}
              startIcon={<Business />}
            >
              Continue to Create New Venue
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ImprovedVenueSearch;